import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Plus, Trash2, Sparkles } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface ReferenceArticle {
  title: string;
  url: string;
  excerpt?: string;
  publishedDate?: string;
}

interface Journalist {
  id?: string;
  sector_id?: number;
  name: string;
  title: string;
  bio?: string;
  photo_url?: string;
  style: 'professional' | 'sporty' | 'business' | 'conversational';
  voice_gender: 'male' | 'female' | 'neutral';
  reference_articles?: ReferenceArticle[];
  sample_questions?: string[];
  system_prompt?: string;
  interview_style_guide?: string;
  is_active: boolean;
}

interface Sector {
  id: number;
  name: string;
}

interface JournalistFormProps {
  open: boolean;
  onClose: () => void;
  journalist?: Journalist | null;
  onSuccess: () => void;
}

export function JournalistForm({ open, onClose, journalist, onSuccess }: JournalistFormProps) {
  const isEdit = !!journalist?.id;

  // Form state
  const [formData, setFormData] = useState<Journalist>({
    name: '',
    title: '',
    bio: '',
    photo_url: '',
    style: 'professional',
    voice_gender: 'neutral',
    reference_articles: [],
    sample_questions: [],
    system_prompt: '',
    interview_style_guide: '',
    is_active: true
  });

  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingPhoto, setGeneratingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load sectors
  useEffect(() => {
    fetchSectors();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (journalist) {
      setFormData({
        ...journalist,
        reference_articles: journalist.reference_articles || [],
        sample_questions: journalist.sample_questions || []
      });
    } else {
      resetForm();
    }
  }, [journalist]);

  const fetchSectors = async () => {
    try {
      const response = await authFetch('/api/sectors');
      if (response.ok) {
        const data = await response.json();
        setSectors(data.sectors || []);
      }
    } catch (err) {
      console.error('Error fetching sectors:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      bio: '',
      photo_url: '',
      style: 'professional',
      voice_gender: 'neutral',
      reference_articles: [],
      sample_questions: [],
      system_prompt: '',
      interview_style_guide: '',
      is_active: true
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = isEdit ? `/api/journalists/${journalist.id}` : '/api/journalists';
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        sectorId: formData.sector_id,
        name: formData.name,
        title: formData.title,
        bio: formData.bio,
        photoUrl: formData.photo_url,
        style: formData.style,
        voiceGender: formData.voice_gender,
        referenceArticles: formData.reference_articles,
        sampleQuestions: formData.sample_questions,
        systemPrompt: formData.system_prompt,
        interviewStyleGuide: formData.interview_style_guide,
        isActive: formData.is_active
      };

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save journalist');
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      console.error('Error saving journalist:', err);
      setError(err instanceof Error ? err.message : 'Failed to save journalist');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePhoto = async () => {
    if (!journalist?.id) {
      setError('Save the journalist first before generating a photo');
      return;
    }

    setGeneratingPhoto(true);
    setError(null);

    try {
      const response = await authFetch(`/api/journalists/${journalist.id}/generate-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to generate photo');
      }

      const data = await response.json();
      setFormData((prev) => ({ ...prev, photo_url: data.photoUrl }));
    } catch (err) {
      console.error('Error generating photo:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate photo');
    } finally {
      setGeneratingPhoto(false);
    }
  };

  const addReferenceArticle = () => {
    setFormData((prev) => ({
      ...prev,
      reference_articles: [
        ...(prev.reference_articles || []),
        { title: '', url: '', excerpt: '', publishedDate: '' }
      ]
    }));
  };

  const updateReferenceArticle = (index: number, field: keyof ReferenceArticle, value: string) => {
    setFormData((prev) => {
      const articles = [...(prev.reference_articles || [])];
      articles[index] = { ...articles[index], [field]: value };
      return { ...prev, reference_articles: articles };
    });
  };

  const removeReferenceArticle = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      reference_articles: prev.reference_articles?.filter((_, i) => i !== index)
    }));
  };

  const addSampleQuestion = () => {
    setFormData((prev) => ({
      ...prev,
      sample_questions: [...(prev.sample_questions || []), '']
    }));
  };

  const updateSampleQuestion = (index: number, value: string) => {
    setFormData((prev) => {
      const questions = [...(prev.sample_questions || [])];
      questions[index] = value;
      return { ...prev, sample_questions: questions };
    });
  };

  const removeSampleQuestion = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      sample_questions: prev.sample_questions?.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Journalist' : 'Create New Journalist'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update journalist details and configuration'
              : 'Create a new AI journalist persona for conducting interviews'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Senior Cycling Journalist"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="200-300 words describing persona and expertise"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sector">Sector</Label>
                <Select
                  value={formData.sector_id?.toString() || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, sector_id: value === "none" ? undefined : parseInt(value) })
                  }
                >
                  <SelectTrigger id="sector">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Sector</SelectItem>
                    {sectors.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id.toString()}>
                        {sector.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="style">Style *</Label>
                <Select value={formData.style} onValueChange={(value: any) => setFormData({ ...formData, style: value })}>
                  <SelectTrigger id="style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="sporty">Sporty</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="conversational">Conversational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voice_gender">Voice Gender</Label>
                <Select
                  value={formData.voice_gender}
                  onValueChange={(value: any) => setFormData({ ...formData, voice_gender: value })}
                >
                  <SelectTrigger id="voice_gender">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 flex items-end">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Photo</h3>

            <div className="space-y-2">
              <Label htmlFor="photo_url">Photo URL</Label>
              <div className="flex gap-2">
                <Input
                  id="photo_url"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  placeholder="https://..."
                />
                {isEdit && (
                  <Button
                    type="button"
                    onClick={handleGeneratePhoto}
                    disabled={generatingPhoto}
                  >
                    {generatingPhoto ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate with AI
                      </>
                    )}
                  </Button>
                )}
              </div>
              {formData.photo_url && (
                <img
                  src={formData.photo_url}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg mt-2"
                />
              )}
            </div>
          </div>

          {/* Reference Articles */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Reference Articles</h3>
              <Button type="button" onClick={addReferenceArticle} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Article
              </Button>
            </div>

            {formData.reference_articles?.map((article, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Article {index + 1}</Label>
                  <Button
                    type="button"
                    onClick={() => removeReferenceArticle(index)}
                    size="sm"
                    variant="ghost"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <Input
                  placeholder="Article title"
                  value={article.title}
                  onChange={(e) => updateReferenceArticle(index, 'title', e.target.value)}
                />
                <Input
                  placeholder="Article URL"
                  value={article.url}
                  onChange={(e) => updateReferenceArticle(index, 'url', e.target.value)}
                />
                <Textarea
                  placeholder="Brief excerpt (optional)"
                  value={article.excerpt || ''}
                  onChange={(e) => updateReferenceArticle(index, 'excerpt', e.target.value)}
                  rows={2}
                />
                <Input
                  type="date"
                  placeholder="Published date (optional)"
                  value={article.publishedDate || ''}
                  onChange={(e) => updateReferenceArticle(index, 'publishedDate', e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* Sample Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Sample Questions</h3>
              <Button type="button" onClick={addSampleQuestion} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>

            {formData.sample_questions?.map((question, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder={`Sample question ${index + 1}`}
                  value={question}
                  onChange={(e) => updateSampleQuestion(index, e.target.value)}
                />
                <Button
                  type="button"
                  onClick={() => removeSampleQuestion(index)}
                  size="sm"
                  variant="ghost"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Advanced Settings */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Advanced Settings</h3>

            <div className="space-y-2">
              <Label htmlFor="system_prompt">System Prompt</Label>
              <Textarea
                id="system_prompt"
                value={formData.system_prompt}
                onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                placeholder="Custom system prompt for this journalist persona"
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="interview_style_guide">Interview Style Guide</Label>
              <Textarea
                id="interview_style_guide"
                value={formData.interview_style_guide}
                onChange={(e) => setFormData({ ...formData, interview_style_guide: e.target.value })}
                placeholder="Guidelines for conducting interviews"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>{isEdit ? 'Update' : 'Create'} Journalist</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
